// Moving a café's players from another system (F-HQ-40, spec/14 §3.5): the HQ screens' server state, as TanStack
// Query hooks over the generated client — `packages/contracts/src/routes/migrations.ts`:
//
//   GET    /v1/hq/organizations/{o}/migrations               the organisation's moves
//   POST   /v1/hq/organizations/{o}/migrations               start one for a venue and a platform
//   GET    /v1/hq/organizations/{o}/migrations/{m}           one, with its mapping, dry run and progress
//   POST   …/{m}/file · PUT …/{m}/mapping · GET …/{m}/rows · PUT …/{m}/ticks · POST …/{m}/import · POST …/{m}/undo
//   GET    …/{m}/report                                       the report, as CSV
//
// All need `hq:organizations` and a second factor; the server checks both on every request. The import is driven from
// here: each call imports batches for a while and answers with the progress, and the page calls again until the move
// is imported — so a closed tab is resumed by opening the move again.

import type { ApiClient, CreateMigrationRun, MigrationRowList, MigrationRun, MigrationRunList, MigrationUndoOutcome, UpdateMigrationMapping } from '@clavian/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';

export const migrationKeys = {
  all: (organizationId: string) => ['hq', 'migrations', organizationId] as const,
  list: (organizationId: string) => ['hq', 'migrations', organizationId, 'list'] as const,
  one: (organizationId: string, migrationId: string) => ['hq', 'migrations', organizationId, 'one', migrationId] as const,
  rows: (organizationId: string, migrationId: string, filter: string) => ['hq', 'migrations', organizationId, 'rows', migrationId, filter] as const,
};

export function useMigrationRuns(organizationId: string) {
  const api = useApi();
  return useQuery<MigrationRunList>({
    queryKey: migrationKeys.list(organizationId),
    queryFn: () => api.listMigrationRuns({ path: { organizationId }, query: { limit: 100 } }),
  });
}

export function useMigrationRun(organizationId: string, migrationId: string) {
  const api = useApi();
  return useQuery<MigrationRun>({
    queryKey: migrationKeys.one(organizationId, migrationId),
    queryFn: () => api.getMigrationRun({ path: { organizationId, migrationId } }),
  });
}

/** The rows with findings — all of them, or one status — a page at a time. */
export function useMigrationRows(organizationId: string, migrationId: string, filter: { status?: MigrationRowList['data'][number]['status']; limit: number }) {
  const api = useApi();
  return useQuery<MigrationRowList>({
    queryKey: migrationKeys.rows(organizationId, migrationId, `${filter.status ?? 'all'}:${filter.limit}`),
    queryFn: () => api.listMigrationRows({ path: { organizationId, migrationId }, query: { limit: filter.limit, ...(filter.status === undefined ? {} : { status: filter.status }) } }),
  });
}

/** Write a run into the cache: what every step answers with. */
function useRemember(organizationId: string) {
  const queryClient = useQueryClient();
  return (run: MigrationRun) => {
    queryClient.setQueryData(migrationKeys.one(organizationId, run.id), run);
    void queryClient.invalidateQueries({ queryKey: migrationKeys.list(organizationId) });
    void queryClient.invalidateQueries({ queryKey: ['hq', 'migrations', organizationId, 'rows', run.id] });
  };
}

export function useStartMigration(organizationId: string) {
  const api = useApi();
  const remember = useRemember(organizationId);
  return useMutation({
    mutationFn: (body: CreateMigrationRun) => api.createMigrationRun({ path: { organizationId }, body }),
    onSuccess: remember,
  });
}

/** Read a picked file as base64, as the upload carries it. */
export function fileAsBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('The file could not be read.'));
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });
}

export function useUploadMigrationFile(organizationId: string, migrationId: string) {
  const api = useApi();
  const remember = useRemember(organizationId);
  return useMutation({
    mutationFn: async (file: File) =>
      api.uploadMigrationFile({ path: { organizationId, migrationId }, body: { fileName: file.name.slice(0, 200), contentBase64: await fileAsBase64(file) } }),
    onSuccess: remember,
  });
}

export function useUpdateMigrationMapping(organizationId: string, migrationId: string) {
  const api = useApi();
  const remember = useRemember(organizationId);
  return useMutation({
    mutationFn: (body: UpdateMigrationMapping) => api.updateMigrationMapping({ path: { organizationId, migrationId }, body }),
    onSuccess: remember,
  });
}

export function useSetMigrationTicks(organizationId: string, migrationId: string) {
  const api = useApi();
  const remember = useRemember(organizationId);
  return useMutation({
    mutationFn: (sourceKeys: readonly string[]) => api.setMigrationTicks({ path: { organizationId, migrationId }, body: { sourceKeys: [...sourceKeys] } }),
    onSuccess: remember,
  });
}

/**
 * Import to the end: one call after another, each answering with the progress (written to the cache, so the page shows
 * it), until the move is imported. A refusal or a failure stops the loop and is the mutation's error.
 */
export function useImportMigration(organizationId: string, migrationId: string) {
  const api = useApi();
  const remember = useRemember(organizationId);
  return useMutation({
    mutationFn: async () => {
      let run = await api.importMigration({ path: { organizationId, migrationId } });
      remember(run);
      for (let calls = 0; run.status === 'importing' && calls < 10_000; calls += 1) {
        run = await api.importMigration({ path: { organizationId, migrationId } });
        remember(run);
      }
      return run;
    },
  });
}

export function useUndoMigration(organizationId: string, migrationId: string) {
  const api = useApi();
  const remember = useRemember(organizationId);
  return useMutation({
    mutationFn: (body: { reason: string; importedPlayerId?: string }) =>
      api.undoMigration({ path: { organizationId, migrationId }, body: { reason: body.reason, ...(body.importedPlayerId === undefined ? {} : { importedPlayerId: body.importedPlayerId as never }) } }),
    onSuccess: (result: MigrationUndoOutcome) => remember(result.run),
  });
}

/**
 * The "account moved" email (F-IAM-13): each call sends the next batch and answers with the run; `resend` starts another
 * round to everyone still waiting. The page calls again while `movedEmail.pending` is above zero.
 */
export function useSendMovedEmail(organizationId: string, migrationId: string) {
  const api = useApi();
  const remember = useRemember(organizationId);
  return useMutation({
    mutationFn: async (resend: boolean) => {
      let run = await api.sendMovedEmail({ path: { organizationId, migrationId }, body: { resend } });
      for (let batch = 0; run.movedEmail.pending > 0 && batch < 200; batch += 1) {
        run = await api.sendMovedEmail({ path: { organizationId, migrationId }, body: { resend: false } });
      }
      return run;
    },
    onSuccess: (run: MigrationRun) => remember(run),
  });
}

export async function downloadMigrationReport(api: ApiClient, organizationId: string, migrationId: string): Promise<string> {
  return api.getMigrationReport({ path: { organizationId, migrationId } }) as Promise<string>;
}
